/**
 * Something holding a derived value that can be dropped when the inputs it was
 * derived from change.
 */
export interface ResettableDerivation {
    /** Drops the held value, so the next read derives it again. */
    reset(): void;
}

/**
 * A value derived once, on first read, and held until it is reset.
 *
 * Exists so that dropping every derived value an object holds is one loop over
 * a list the values put themselves on, rather than a hand-written roll call
 * that has to be kept in step with the fields. A value omitted from such a roll
 * call outlives the inputs it was derived from and is then reported as current
 * — which is why registration is a required constructor argument here: a
 * derived value that is not resettable cannot be constructed.
 *
 * Holding is unconditional on the derived value: `undefined`, `null` and an
 * empty array are held like anything else, so a derivation that legitimately
 * yields one is performed once rather than on every read.
 *
 * A derivation that throws holds nothing, so the next read attempts it again.
 */
export class DerivedValue<T> implements ResettableDerivation {
    private held = false;
    private value: T | undefined;

    /**
     * @param derive Computes the value. Called at most once per reset, on the
     * first read after it.
     * @param derivations The list this value registers itself on, which its
     * owner resets as a whole.
     */
    public constructor(
        private readonly derive: () => T,
        derivations: ResettableDerivation[]
    ) {
        derivations.push(this);
    }

    public get(): T {
        if (!this.held) {
            this.value = this.derive();
            this.held = true;
        }
        return this.value as T;
    }

    public reset(): void {
        this.held = false;
        this.value = undefined;
    }
}

/**
 * A {@link DerivedValue} derived from one argument, holding the most recent
 * argument's result only.
 *
 * Single-entry by design: the arguments in use are a small, stable set (a bin
 * count a view was built with), so an unbounded map would retain a result per
 * argument ever asked for without saving a derivation that is actually
 * repeated.
 */
export class DerivedValueByKey<K, T> implements ResettableDerivation {
    private held = false;
    private key: K | undefined;
    private value: T | undefined;

    public constructor(
        private readonly derive: (key: K) => T,
        derivations: ResettableDerivation[]
    ) {
        derivations.push(this);
    }

    public get(key: K): T {
        if (!this.held || this.key !== key) {
            this.value = this.derive(key);
            this.key = key;
            this.held = true;
        }
        return this.value as T;
    }

    public reset(): void {
        this.held = false;
        this.key = undefined;
        this.value = undefined;
    }
}
